// First, install recharts
// npm install recharts

import React, { useState, useEffect, useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";

// Format large numbers compactly for Y-axis labels
const formatLargeNumber = (value: number): string => {
  const absValue = Math.abs(value);
  const sign = value < 0 ? "-" : "";

  if (absValue >= 1000000) {
    return sign + (absValue / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
  }
  if (absValue >= 1000) {
    return sign + (absValue / 1000).toFixed(1).replace(/\.0$/, "") + "K";
  }
  return value.toString();
};

interface DataPoint {
  name?: string;
  sales?: number;
  revenue?: number;
  timestamp?: number;
  [key: string]: string | number | null | undefined;
}


interface VerticalMarkers {
  [key: string]: number;
}

const SHORT_LABELS: { [key: string]: string } = {
  "Not Started": "NS",
  "Inning 1": "1",
  "Inning 2": "2",
  "Inning 3": "3",
  "Inning 4": "4",
  "Inning 5": "5",
  "Inning 6": "6",
  "Inning 7": "7",
  "Inning 8": "8",
  "Inning 9": "9",
  Postponed: "POST",
  Cancelled: "CANC",
  Interrupted: "INTR",
  Abandoned: "ABD",
  Finished: "FT",
  "Quarter 1": "1",
  "Quarter 2": "2",
  "Quarter 3": "3",
  "Quarter 4": "4",
  "Over Time": "OT",
  "Break Time": "BT",
  Halftime: "HT",
  "Game Finished": "FT",
  "After Over Time": "FT",
  "Game Postponed": "POST",
  "Game Cancelled": "CANC",
  "Game Suspended": "SUSP",
  "Game Awarded": "AWD",
  "Game Abandoned": "ABD",
  "First Half, Kick Off": "1",
  "Second Half, 2nd Half Started": "2",
  "First Half": "1",
  "Second Half": "2",
  "Kick Off": "1",
  "2nd Half Started": "2",
  "Extra Time": "ET",
  "Penalty In Progress": "P",
  "Match Suspended": "SUSP",
  "Match Interrupted": "INT",
  "Match Finished": "FT",
  "Match Postponed": "PST",
  "Match Cancelled": "CANC",
  "Match Abandoned": "ABD",
  "First Period": "1",
  "Second Period": "2",
  "Third Period": "3",
  "Penalties Time": "PT",
  "After Penalties": "FT",
  "First Quarter": "1",
  "Second Quarter": "2",
  "Third Quarter": "3",
  "Fourth Quarter": "4",
};

const getShortLabel = (labelName: string): string =>
  labelName in SHORT_LABELS ? SHORT_LABELS[labelName] : labelName;

const formatPregameTime = (negativeMinutes: number): string => {
  const total = Math.abs(Math.round(negativeMinutes));
  const days = Math.floor(total / 1440);
  const hours = Math.floor((total % 1440) / 60);
  const mins = total % 60;
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (mins > 0 || parts.length === 0) parts.push(`${mins}m`);
  return parts.join(" ");
};

const setTicks = (min: number, max: number, frequency: number): number[] => {
  if (frequency === 0) {
    return [];
  }

  // Limit max number of ticks to prevent performance issues with large ranges
  const maxTicks = 20;
  const range = max - min;
  const estimatedTicks = range / frequency;

  // If too many ticks would be generated, calculate a nice round frequency
  let adjustedFrequency = frequency;
  if (estimatedTicks > maxTicks) {
    // Calculate raw frequency needed
    const rawFrequency = range / maxTicks;
    // Round to a nice number (1, 2, 5, 10, 20, 50, 100, 200, 500, etc.)
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawFrequency)));
    const normalized = rawFrequency / magnitude;
    let niceNormalized: number;
    if (normalized <= 1) {
      niceNormalized = 1;
    } else if (normalized <= 2) {
      niceNormalized = 2;
    } else if (normalized <= 5) {
      niceNormalized = 5;
    } else {
      niceNormalized = 10;
    }
    adjustedFrequency = niceNormalized * magnitude;
  }

  // Use the exact min/max passed in (which match the domain) to avoid
  // grid lines not reaching the edges or duplicate lines at boundaries
  const ticks: number[] = [min];

  // Find the first tick value above min that's a multiple of adjustedFrequency
  const firstInternalTick =
    Math.ceil(min / adjustedFrequency) * adjustedFrequency;

  for (let i = firstInternalTick; i < max; i += adjustedFrequency) {
    // Round to avoid floating point precision issues
    const roundedTick = Math.round(i * 1000000) / 1000000;
    // Avoid duplicate if min happens to be on a tick boundary
    if (roundedTick > min) {
      ticks.push(roundedTick);
    }
  }

  // Always include max to ensure grid reaches the top
  ticks.push(max);

  return ticks;
};

interface TooltipPayload {
  name: string;
  value: number;
  color: string;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayload[];
  label?: string | number;
  showPlusSign?: boolean;
  filledValues?: Map<number, Record<string, number>>;
  books?: string[];
  itemColors?: Record<string, string>;
  itemsState?: Record<string, boolean>;
}

// Defined at module scope so Recharts sees a stable component type — a
// component declared inside RealData is a brand-new type on every render,
// forcing the tooltip subtree to unmount/remount on each poll.
const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  label,
  showPlusSign = true,
  filledValues,
  books,
  itemColors,
  itemsState,
}) => {
  // Prefer every visible book's last known value at this minute (see
  // filledValues in RealData) over Recharts' payload, which only holds the
  // books that have a point in the hovered entry.
  const filled = filledValues?.get(Number(label));
  const rows: TooltipPayload[] =
    filled && books && itemColors
      ? books
          .filter((book) => itemsState?.[book] !== false && filled[book] !== undefined)
          .map((book) => ({ name: book, value: filled[book], color: itemColors[book] }))
      : payload ?? [];
  if (active && rows.length) {
    const minuteVal = Number(label);
    const isPregame = minuteVal < 0;
    const timeLabel = isPregame
      ? `Before Start: ${formatPregameTime(minuteVal)}`
      : `Minute: ${minuteVal}`;
    return (
      <div
        style={{
          backgroundColor: "#000",
          padding: "12px",
          border: "1px solid #007bff",
          borderRadius: "5px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <p
          style={{
            fontWeight: "bold",
            marginBottom: "8px",
            borderBottom: "1px solid #444",
            paddingBottom: "5px",
            color: "#fff",
          }}
        >
          {timeLabel}
        </p>
        {rows.map((entry, index) => (
          <p
            key={`item-${index}`}
            style={{
              color: entry.color,
              margin: "5px 0",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <span>{entry.name}:</span>
            <span style={{ marginLeft: "15px", fontWeight: "500" }}>
              {typeof entry.value === "number" &&
              entry.value > 0 &&
              showPlusSign
                ? "+" + entry.value
                : entry.value}
            </span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

interface RealDataProps {
  data: DataPoint[];
  books: string[];
  itemColors: Record<string, string>;
  isLiveGame: boolean;
  startTime: number;
  isMoneyline: boolean;
  vertical_markers: VerticalMarkers;
  game_part_name: string;
  showPlusSign?: boolean;
}

const RealData: React.FC<RealDataProps> = ({
  data,
  books,
  itemColors,
  isLiveGame,
  startTime,
  isMoneyline,
  vertical_markers,
  game_part_name,
  showPlusSign = true,
}) => {
  interface ItemsState {
    [key: string]: boolean;
  }

  const [itemsState, setItemsState] = useState<ItemsState>(() =>
    books.reduce<ItemsState>((acc, item) => {
      acc[item] = true;
      return acc;
    }, {}),
  );
  const [screenWidth, setScreenWidth] = useState<number>(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    // Cleanup listener on unmount
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Function to toggle visibility
  const toggleSeries = (dataKey: string) => {
    setItemsState((prevState) => ({
      ...prevState,
      [dataKey]: !prevState[dataKey],
    }));
  };

  const { dataRange, latestTimestamp } = useMemo(() => {
    const range = {
      dataMin: 1000000,
      dataMax: -1000000,
      maxTime: 0,
      minTime: 0,
    };
    let latest = -Infinity;
    for (const entry in data) {
      let possibleTime = 0;
      Object.entries(data[entry]).forEach(([key, value]) => {
        if (key !== "timestamp") {
          if (itemsState[key] === true) {
            if (value !== null && typeof value === "number") {
              if (value < range.dataMin) {
                range.dataMin = value;
              }
              if (value > range.dataMax) {
                range.dataMax = value;
              }
              if (possibleTime > range.maxTime) {
                range.maxTime = possibleTime;
                possibleTime = 0;
              }
            }
          }
        } else {
          if (value !== null && typeof value === "number") {
            if (value > range.maxTime) {
              possibleTime = value;
            }
            if (value < range.minTime) {
              range.minTime = value;
            }
            if (value > latest) {
              latest = value;
            }
          }
        }
      });
    }
    return { dataRange: range, latestTimestamp: latest };
  }, [data, itemsState]);

  // The backend only sends a book's value where its line changes, so most
  // entries hold just a few books. Carry each book's last value forward —
  // between its first and last point only, so a book isn't shown before it
  // posted or after it stopped — keyed by timestamp for the tooltip.
  const filledValues = useMemo(() => {
    const lastIndex: Record<string, number> = {};
    data.forEach((entry, i) => {
      for (const book of books) {
        if (typeof entry[book] === "number") lastIndex[book] = i;
      }
    });
    const current: Record<string, number> = {};
    const byTimestamp = new Map<number, Record<string, number>>();
    data.forEach((entry, i) => {
      for (const book of books) {
        const value = entry[book];
        if (typeof value === "number") current[book] = value;
        else if (lastIndex[book] !== undefined && i > lastIndex[book]) delete current[book];
      }
      if (typeof entry.timestamp === "number") byTimestamp.set(entry.timestamp, { ...current });
    });
    return byTimestamp;
  }, [data, books]);

  // Calculate the min and max values in the data
  let yAxisMin = 0;
  let yAxisMax = 0;
  let adjustment = 2;
  let yTickRange: number | undefined;
  if (isMoneyline) {
    const baseRange = dataRange.dataMax - dataRange.dataMin;
    let basis = Math.round(baseRange / 100) * 10;
    if (basis <= 100) {
      basis = 100;
    } else if (basis <= 200) {
      basis = 200;
    } else if (basis <= 500) {
      basis = 500;
    } else if (basis <= 1000) {
      basis = 1000;
    } else if (basis <= 2000) {
      basis = 2000;
    } else if (basis <= 5000) {
      basis = 5000;
    } else if (basis <= 10000) {
      basis = 10000;
    } else if (basis <= 20000) {
      basis = 20000;
    } else if (basis <= 50000) {
      basis = 50000;
    } else if (basis <= 100000) {
      basis = 100000;
    } else if (basis <= 200000) {
      basis = 200000;
    } else if (basis <= 500000) {
      basis = 500000;
    } else if (basis <= 1000000) {
      basis = 1000000;
    }
    const yAxisMinTemp = Math.floor(dataRange.dataMin / basis) * basis;
    yAxisMin =
      yAxisMinTemp == dataRange.dataMin ? yAxisMinTemp - basis : yAxisMinTemp;
    const yAxisMaxTemp = Math.ceil(dataRange.dataMax / basis) * basis;
    yAxisMax =
      yAxisMaxTemp == dataRange.dataMax ? yAxisMaxTemp + basis : yAxisMaxTemp;
    yTickRange = basis;
    adjustment = basis;
  } else {
    // Calculate nice round intervals for non-moneyline data (spread/over-under)
    const baseRange = dataRange.dataMax - dataRange.dataMin;

    // Determine tick interval based on data range
    let tickInterval = 1;
    if (baseRange <= 5) {
      tickInterval = 0.5;
      adjustment = 0.5;
    } else if (baseRange <= 10) {
      tickInterval = 1;
      adjustment = 1;
    } else if (baseRange <= 20) {
      tickInterval = 2;
      adjustment = 2;
    } else if (baseRange <= 50) {
      tickInterval = 5;
      adjustment = 5;
    } else {
      tickInterval = 10;
      adjustment = 10;
    }

    yTickRange = tickInterval;

    // Calculate the nearest multiple of adjustment below the minimum
    const yAxisMinTemp =
      Math.floor(dataRange.dataMin / adjustment) * adjustment;
    yAxisMin =
      yAxisMinTemp == dataRange.dataMin
        ? yAxisMinTemp - adjustment
        : yAxisMinTemp;

    // Calculate the nearest multiple of adjustment above the maximum
    const yAxisMaxTemp = Math.ceil(dataRange.dataMax / adjustment) * adjustment;
    yAxisMax =
      yAxisMaxTemp == dataRange.dataMax
        ? yAxisMaxTemp + adjustment
        : yAxisMaxTemp;
  }

  // Live game right edge: round max timestamp up to nearest 30 min
  const xAxisMaxTemp = Math.ceil(dataRange.maxTime / 30) * 30;
  const xAxisMax = xAxisMaxTemp == 0 ? 30 : xAxisMaxTemp;

  // Pregame left edge: round earliest timestamp down to nearest 30 min
  const xAxisMinTemp = Math.floor(dataRange.minTime / 30) * 30;
  const xAxisMin = xAxisMinTemp === 0 ? -30 : xAxisMinTemp;

  // Pregame right edge: most recent data point, rounded up to nearest 30 min, capped at 0
  const xAxisRightPregame =
    latestTimestamp > -Infinity
      ? Math.min(0, Math.ceil(latestTimestamp / 30) * 30)
      : 0;

  interface ReferencePoint {
    x: number;
    label: string;
  }

  const referencePoints: ReferencePoint[] = [];
  for (const marker in vertical_markers) {
    if (referencePoints.length === 0 && marker == "Finished") {
      continue;
    }
    if (getShortLabel(marker) === "1") {
      continue;
    }
    referencePoints.push({
      x: (vertical_markers[marker] - startTime) / 60,
      label: marker,
    });
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "center", flexWrap: "wrap", padding: "12px 0" }}>
        {books.map((book) => (
          <div
            key={book}
            onClick={() => toggleSeries(book)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              margin: "4px 10px",
              cursor: "pointer",
              opacity: itemsState[book] !== false ? 1 : 0.5,
            }}
          >
            <div style={{ width: 10, height: 10, backgroundColor: itemColors[book], marginRight: 5, flexShrink: 0 }} />
            <span className="font_family_base">{book}</span>
          </div>
        ))}
      </div>
    <ResponsiveContainer width="100%" height={600}>
      <LineChart
        data={data}
        margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="timestamp"
          label={{
            value: game_part_name,
            position: "insideBottom",
            style: {
              fontFamily: "Diphylleia, serif",
            },
            offset: -10, // Move label up (negative value)
            dy: 10, // Add extra space below the axis
          }}
          tickLine={false}
          axisLine={false}
          tick={false}
          domain={isLiveGame ? [0, xAxisMax] : [xAxisMin, xAxisRightPregame]}
          type="number"
          allowDataOverflow={true}
        />
        <YAxis
          domain={[yAxisMin, yAxisMax]}
          ticks={
            yTickRange !== undefined
              ? setTicks(yAxisMin, yAxisMax, yTickRange)
              : setTicks(yAxisMin, yAxisMax, 2)
          }
          tickFormatter={formatLargeNumber}
          tick={{ fontFamily: "Diphylleia, serif" }}
          label=""
          width={60}
        />
        {/* Add reference lines for specific points */}
        {referencePoints.map((point, index) => (
          <ReferenceLine
            key={`ref-line-${index}`}
            x={point.x}
            stroke="#ff7300"
            strokeDasharray="3 3"
            label={{
              value:
                screenWidth > 1000
                  ? getShortLabel(point.label)
                  : getShortLabel(point.label),
              position: "bottom",
              style: {
                fontFamily: "Diphylleia, serif",
              },
              fill: "#ff7300",
              fontSize: 12,
              dy: 20, // Offset to position label below the x-axis
            }}
          />
        ))}
        <Tooltip
          content={
            <CustomTooltip
              showPlusSign={showPlusSign}
              filledValues={filledValues}
              books={books}
              itemColors={itemColors}
              itemsState={itemsState}
            />
          }
          isAnimationActive={false}
          cursor={false} // This hides the vertical reference line
        />
        {books.map((item) => (
          <Line
            key={item}
            type="monotone"
            dataKey={item}
            stroke={itemColors[item]}
            hide={!itemsState[item]}
            connectNulls={true}
            strokeWidth={2}
            dot={false} // Hide normal dots
            activeDot={{ r: 7, strokeWidth: 1 }} // Keep active dot when hovering
            // The chart is fed fresh data by polling; animating every update
            // interpolates every point of every line each frame and piles up
            // garbage on long live games.
            isAnimationActive={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
    </div>
  );
};

interface MyChartProps {
  data?: DataPoint[];
  books: string[];
  bookColors: Record<string, string>;
  isLiveGame: boolean;
  startTime: number;
  isMoneyline: boolean;
  vertical_markers: VerticalMarkers;
  game_part_name: string;
  showPlusSign?: boolean;
}

const MyChart: React.FC<MyChartProps> = ({
  data,
  books,
  bookColors,
  isLiveGame,
  startTime,
  isMoneyline,
  vertical_markers,
  game_part_name,
  showPlusSign = true,
}) => {
  return data && data.length > 0 ? (
    <RealData
      data={data}
      books={books}
      itemColors={bookColors}
      isLiveGame={isLiveGame}
      startTime={startTime}
      isMoneyline={isMoneyline}
      vertical_markers={vertical_markers}
      game_part_name={game_part_name}
      showPlusSign={showPlusSign}
    />
  ) : (
    <div style={{ textAlign: "center", padding: "40px 20px", color: "#888" }}>
      <p className="font_family_base" style={{ fontSize: 16 }}>No game data available.</p>
    </div>
  );
};

// Memoized so the parent's unrelated re-renders (e.g. the games-list poll)
// don't re-render the chart when its data hasn't changed.
export default React.memo(MyChart);
